/**
 * @fileoverview Constants for the Coasting Detection DSP Pipeline
 *
 * These constants control the behavior of the Digital Signal Processing (DSP)
 * pipeline used to detect coasting periods in motorcycle racing telemetry.
 *
 * The values have been empirically tuned for GPS data sampled at ~5Hz with
 * typical GPS noise characteristics.
 */

/**
 * Distance bucket size in meters for spatial sampling.
 * Using 5m intervals provides good balance between resolution and noise reduction.
 */
export const DISTANCE_BUCKET_SIZE_METERS = 5

/**
 * Window size for final intensity smoothing (in data points).
 * Larger window = smoother output but less responsive to rapid changes.
 */
export const FINAL_SMOOTHING_WINDOW_POINTS = 7

/**
 * Gaussian spread factor for final smoothing.
 * Controls the shape of the Gaussian kernel used for smoothing.
 */
export const FINAL_SMOOTHING_GAUSSIAN_SPREAD = 0.5

/**
 * Hard braking threshold in m/s².
 * Braking harder than -6 m/s² (~0.6g) is always productive riding.
 */
export const HARD_BRAKING_THRESHOLD_MS2 = -6.0

/**
 * Hard acceleration threshold in m/s².
 * Acceleration above 3 m/s² is considered productive (full throttle).
 */
export const HARD_ACCELERATION_THRESHOLD_MS2 = 3.0

/**
 * Threshold for detecting significant acceleration in m/s².
 * Used for transition detection (throttle-to-brake, brake-to-throttle).
 */
export const SIGNIFICANT_ACCELERATION_MS2 = 3.0

/**
 * Threshold for detecting significant braking in m/s².
 * Used for transition detection (throttle-to-brake, brake-to-throttle).
 */
export const SIGNIFICANT_BRAKING_MS2 = -3.0

/**
 * Fraction of the speed-dependent maximum acceleration that counts as full
 * throttle (allows for GPS noise and the rider not being quite WOT).
 */
export const FULL_THROTTLE_FRACTION = 0.6

/**
 * Minimum significance for a coasting section to be kept, in "coasting-seconds"
 * (the integral of intensity over elapsed time). A section fully coasting
 * (intensity 1.0) for 1s scores 1.0; a 0.4-intensity blip lasting 0.3s scores
 * ~0.12. Sections below this represent too little wasted time to be worth
 * showing and are treated as noise. Tunable: raise to hide more, lower to keep
 * more. Judged on integrated time, not peak height or point count, so a
 * tall-narrow spike and a low-wide wobble are filtered on the same fair scale.
 */
export const SPIKE_REMOVAL_MIN_SIGNIFICANCE = 0.5

/**
 * Threshold for detecting isolated coasting points (intensity value).
 * Points above 0.3 surrounded by non-coasting = likely anomaly.
 */
export const ISOLATED_COASTING_THRESHOLD = 0.3

/**
 * Threshold for detecting significant coasting in context window.
 * Used to determine if surrounding points show real coasting pattern.
 */
export const SIGNIFICANT_COASTING_THRESHOLD = 0.2

/**
 * Window size (data points) for anomaly detection context.
 * Checks ±3 points around current point to detect isolated anomalies.
 */
export const ANOMALY_DETECTION_WINDOW_POINTS = 3

/**
 * Reduction factor when filling anomalous gaps.
 * Filled values are reduced by 0.7x to be conservative.
 */
export const ANOMALY_FILL_REDUCTION_FACTOR = 0.7

/**
 * Maximum gap length (data points) for interpolation.
 * Gaps of 1-3 points can be interpolated to create continuity.
 */
export const GAP_FILL_MAX_LENGTH_POINTS = 3

/**
 * Minimum surrounding intensity for gap filling.
 * Both sides of gap must have intensity ≥ 0.25 to trigger interpolation.
 */
export const GAP_FILL_MIN_SURROUNDING_INTENSITY = 0.25

/**
 * Threshold for classifying a point as "coasting" (intensity value).
 * Values below 0.15 (15%) are considered non-coasting.
 */
export const COASTING_DETECTION_THRESHOLD = 0.15

/**
 * Post-smoothing suppression threshold.
 * After smoothing, values < 0.1 are artifacts and set to zero.
 */
export const POST_SMOOTHING_SUPPRESSION_THRESHOLD = 0.1

/**
 * Threshold for determining if smoothed acceleration is near zero, in m/s².
 * When smoothed acceleration is below this, the signal may have been crushed.
 */
export const NEAR_ZERO_ACCELERATION_THRESHOLD_MS2 = 0.5

/**
 * Minimum raw acceleration signal to be considered significant, in m/s².
 * Used when smoothed signal is near zero to preserve actual acceleration/braking;
 * low enough to catch gentle braking.
 */
export const MIN_RAW_ACCELERATION_SIGNAL_MS2 = 0.8
